import { useEffect, useRef, useState } from 'react';
import { UserActionRequest_ActionType } from '@weave/schema-gen-ts/dist/schemas/threads-of-success/v2/api.pb';
import { ThreadsOfSuccessV2Mutations } from '@frontend/api-threads-of-success-v2';
import { useAppScopeStore } from '@frontend/scope';
import { WeaveRewindPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlModalProps, SpinningLoader } from '@frontend/design-system';
import introAnimation from './animations/intro.json';
import backgroundMusic from './assets/badass.mp3';
import { ModalHeader } from './modal-header';
import { ProgressBar } from './progress-bar';
import { ShareVideo } from './share-video';
import { CUSTOM_COLOR, fontFamily, translucentButtonStyles, weaveRewindFontFaces } from './styles';
import { WeaveRewindAnimation } from './weave-rewind-animation';

type Props = {
  modalProps: ModalControlModalProps;
  shorts: { type: string; animation: any }[];
};

export const WeaveRewindModal = ({ modalProps, shorts }: Props) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  const [selectedAnimationIndex, setSelectedAnimationIndex] = useState<number>(-1); // Start at -1 for intro
  const [animationProgress, setAnimationProgress] = useState<number>(0);
  const [lottieLoading, setLottieLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lottieRef = useRef<any>(null);

  const { mutate: mutateUserAction } = ThreadsOfSuccessV2Mutations.useUserAction();

  const isIntroScreen = selectedAnimationIndex === -1;
  const shortsCount = shorts.length;
  const showAnimationScreens = selectedAnimationIndex >= 0 && selectedAnimationIndex < shortsCount;

  // Track animation progress
  useEffect(() => {
    setAnimationProgress(0);
    setIsPaused(false); // Reset pause state when animation changes
  }, [selectedAnimationIndex]);

  // Wait for fonts to load
  useEffect(() => {
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    } else {
      setFontsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!modalProps.show) {
      setSelectedAnimationIndex(-1); // Reset to intro
      setLottieLoading(true);

      // Stop and reset audio when modal closes
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }

    // Show loading state only for the first animation when modal opens
    if (lottieLoading && modalProps.show && fontsLoaded) {
      setTimeout(() => {
        setLottieLoading(false);
      }, 500); // Simulate loading time
    }
  }, [lottieLoading, modalProps.show, fontsLoaded]);

  // Start audio when modal opens and intro begins
  useEffect(() => {
    if (!lottieLoading && modalProps.show && audioRef.current && isIntroScreen) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.warn('Audio autoplay blocked:', error);
        // Autoplay might be blocked by browser - user will need to interact first
      });
    }
  }, [isIntroScreen, lottieLoading, modalProps.show]);

  const onClose = () => {
    modalProps.onClose();
    mutateUserAction({
      locationIds,
      actionType: UserActionRequest_ActionType.ACTION_TYPE_DISMISS_SHORTS,
    });
  };

  const onDownload = () => {
    // TODO: implement download functionality here
    mutateUserAction({
      locationIds,
      actionType: UserActionRequest_ActionType.ACTION_TYPE_DOWNLOAD_SHORTS,
    });
  };

  const onNextAnimation = () => {
    setAnimationProgress(0);
    setIsPaused(false);
    setSelectedAnimationIndex((prev) => {
      const nextIndex = prev < shortsCount ? prev + 1 : shortsCount;

      // Resume audio if it was paused
      if (isPaused && audioRef.current) {
        setIsPaused(false);
        audioRef.current.play().catch(console.error);
      }

      // Stop audio when reaching the final slide
      if (nextIndex >= shortsCount && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      return nextIndex;
    });
  };

  const onPlayOrPause = () => {
    setIsPaused((prev) => {
      const newPaused = !prev;

      // Sync audio with animation pause/play
      if (audioRef.current) {
        if (newPaused) {
          audioRef.current.pause();
        } else {
          audioRef.current.play().catch(console.error);
        }
      }

      return newPaused;
    });
  };

  const toggleMute = () => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      if (audioRef.current) {
        audioRef.current.muted = newMuted;
      }
      return newMuted;
    });
  };

  const onPreviousAnimation = () => {
    setAnimationProgress(0);
    setIsPaused(false);
    setSelectedAnimationIndex((prev) => {
      const prevIndex = prev > -1 ? prev - 1 : -1;

      // Resume audio if it was paused
      if (isPaused && audioRef.current) {
        setIsPaused(false);
        audioRef.current.play().catch(console.error);
      }

      // Restart audio when going back from final screen to shorts
      if (prev >= shortsCount && audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      }

      return prevIndex;
    });
  };

  const onWatchAgain = () => {
    setIsPaused(false);
    setSelectedAnimationIndex(-1); // Start from intro, not first short

    // Restart audio when watching again
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(console.error);
    }

    mutateUserAction({
      locationIds,
      actionType: UserActionRequest_ActionType.ACTION_TYPE_REWATCH_SHORTS_VIDEO,
    });
  };

  return (
    <>
      <Modal
        {...modalProps}
        minWidth={400}
        css={[
          {
            background: CUSTOM_COLOR,
            padding: 0,
            height: 732,
            fontFamily,
            '.wv-modal-header': {
              position: 'absolute',
              top: theme.spacing(-5),
              left: 0,
              width: '100%',
              padding: 0,
              background: 'transparent',
              h2: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              },
            },
            '.wv-modal-flex-content': {
              padding: theme.spacing(0, 1.5),
            },
          },
          weaveRewindFontFaces,
        ]}
      >
        <ModalHeader isMuted={isMuted} onClose={onClose} onDownload={onDownload} toggleMute={toggleMute} />
        <Modal.Body>
          {/* Background music audio element */}
          <audio
            ref={audioRef}
            src={backgroundMusic}
            loop={true}
            muted={isMuted}
            preload='auto'
            css={{ display: 'none' }}
          />

          {/* Spinner while loading */}
          {lottieLoading && (
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <SpinningLoader color='white' />
            </div>
          )}

          {/* Intro Animation */}
          {isIntroScreen && !lottieLoading && (
            <WeaveRewindAnimation
              animation={introAnimation}
              isPaused={isPaused}
              key='intro'
              lottieRef={lottieRef}
              onNextAnimation={onNextAnimation}
              onPlayOrPause={onPlayOrPause}
            />
          )}

          {/* Weave Rewind Animations */}
          {showAnimationScreens && shorts[selectedAnimationIndex]?.animation && !lottieLoading && (
            <WeaveRewindAnimation
              animation={shorts[selectedAnimationIndex].animation}
              isPaused={isPaused}
              key={selectedAnimationIndex}
              lottieRef={lottieRef}
              onNextAnimation={onNextAnimation}
              onPlayOrPause={onPlayOrPause}
            />
          )}

          {/* Share Video Screen */}
          {selectedAnimationIndex >= shortsCount && (
            <ShareVideo onDownload={onDownload} onModalClose={modalProps.onClose} onWatchAgain={onWatchAgain} />
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* Only show progress bars for shorts, not intro */}
          {showAnimationScreens && (
            <ProgressBar
              animationProgress={animationProgress}
              lottieRef={lottieRef}
              selectedAnimationIndex={selectedAnimationIndex}
              setAnimationProgress={setAnimationProgress}
              shorts={shorts}
            />
          )}
          {/* Show previous button except on intro screen */}
          {selectedAnimationIndex > -1 && (
            <Button
              css={[
                translucentButtonStyles,
                { position: 'absolute', left: -40, top: '50%', transform: 'translateY(-50%)' },
              ]}
              iconName='caret-left'
              onClick={onPreviousAnimation}
              trackingId={WeaveRewindPrefixes.Modal + '-previous-animation-button'}
            />
          )}
          {/* Show next button on intro and all shorts except the share video screen */}
          {(isIntroScreen || showAnimationScreens) && (
            <Button
              css={[
                translucentButtonStyles,
                { position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)' },
              ]}
              iconName='caret-right'
              onClick={onNextAnimation}
              trackingId={WeaveRewindPrefixes.Modal + '-next-animation-button'}
            />
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
